// src/components/pages/donations/LandListPage.tsx
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import LandCard from "./LandCard";
import LandFilters, { FilterState } from "./LandFilters";
import { listDonationLands } from "../../../services/donationsService";
import { LandListItem } from "../../../types/donations";
import MapModal from "../../maps/MapModal";
import Breadcrumbs from "../../../components/navigation/Breadcrumbs";

// small helper to parse positive ints with default
const toInt = (v: string | null, d: number) => {
  const n = v ? parseInt(v, 10) : NaN;
  return Number.isFinite(n) && n > 0 ? n : d;
};

const LandListPage: React.FC = () => {
  const [sp, setSp] = useSearchParams();

  // paging from URL
  const pageNo = toInt(sp.get("pageNo"), 1);
  const pageSize = toInt(sp.get("pageSize"), 12);
  const searchQuery = sp.get("searchKeyword") || "";

  // initial filters from URL
  const initial: FilterState = useMemo(
  () => ({
    regionId: sp.get("regionId") ? Number(sp.get("regionId")) : null,
    cityId: sp.get("cityId") ? Number(sp.get("cityId")) : null,
    centerId: sp.get("centerId") ? Number(sp.get("centerId")) : null,
    minPrice: sp.get("minPrice") ? Number(sp.get("minPrice")) : null,
    maxPrice: sp.get("maxPrice") ? Number(sp.get("maxPrice")) : null,
    searchKeyword: sp.get("searchKeyword") || "",
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [sp.toString()]
);

  // list state
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<LandListItem[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // map modal state
  const [mapItem, setMapItem] = useState<LandListItem | null>(null);
  const openMap = (it: LandListItem) => setMapItem(it);
  const closeMap = () => setMapItem(null);

  // fetch function
  async function load() {
    setLoading(true);
    try {
      const res = await listDonationLands({
        regionId: initial.regionId ?? undefined,
        cityId: initial.cityId ?? undefined,
        centerId: initial.centerId ?? undefined,
        minPrice: initial.minPrice ?? undefined,
        maxPrice: initial.maxPrice ?? undefined,
        searchKeyword: searchQuery.trim() || undefined,
        pageNo,
        pageSize,
      });
      setItems(res.items);
      setPageCount(res.pageCount);
      setTotalCount(res.totalCount);
    } finally {
      setLoading(false);
    }
  }

  // load on URL param changes
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp.toString()]);

  // apply filters -> write to URL & reset page to 1
  function applyFilters(f: FilterState) {
  const next = new URLSearchParams(sp);

  const setOrDel = (k: string, v?: number | null) => {
      if (v == null || Number.isNaN(v)) next.delete(k);
      else next.set(k, String(v));
    };

    setOrDel("regionId", f.regionId);
    setOrDel("cityId", f.cityId);
    setOrDel("centerId", f.centerId);
    setOrDel("minPrice", f.minPrice);
    setOrDel("maxPrice", f.maxPrice);

    if (f.searchKeyword?.trim()) {
      next.set("searchKeyword", f.searchKeyword.trim());
    } else {
      next.delete("searchKeyword");
    }

    next.set("pageNo", "1");
    next.set("pageSize", String(pageSize));
    setSp(next, { replace: false });
  }
  // pager
  const go = (n: number) => {
    const next = new URLSearchParams(sp);
    next.set("pageNo", String(n));
    next.set("pageSize", String(pageSize));
    setSp(next, { replace: false });
  };

  // 🔽 NEW: page-size change -> reset to page 1 and persist in URL
  const changePageSize = (n: number) => {
    const next = new URLSearchParams(sp);
    next.set("pageNo", "1");
    next.set("pageSize", String(n));
    setSp(next, { replace: false });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">
      <div className="space-y-6">
        {/* search bar */}
        <div className="d-flex justify-content-center mb-4">
        </div>
        <Breadcrumbs className="mb-4" />

        {/* search results meta */}
        {searchQuery && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="text-muted small">
            نتائج البحث عن: <strong>"{searchQuery}"</strong>
          </div>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              const next = new URLSearchParams(sp);
              next.delete("searchKeyword");
              next.set("pageNo", "1");
              setSp(next, { replace: false });
            }}
          >
            مسح البحث
          </button>
        </div>
      )}

        {/* filters */}
        <LandFilters initial={initial} onApply={applyFilters} />

        {/* result meta */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="text-muted small">إجمالي النتائج: {totalCount}</div>
        </div>

        {/* Card grid: 1 / 2 / 3 per row */}
        <div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="land-bar-item">
                  <div className="land-bar-container">
                    <div className="land-bar-left">
                      <div className="land-bar-location">
                        <div className="location-icon">
                          <div
                            className="placeholder"
                            style={{
                              width: "18px",
                              height: "18px",
                              borderRadius: "50%",
                            }}
                          ></div>
                        </div>
                        <div className="location-text">
                          <div
                            className="placeholder"
                            style={{
                              height: "20px",
                              width: "200px",
                              marginBottom: "4px",
                            }}
                          ></div>
                          <div
                            className="placeholder"
                            style={{ height: "16px", width: "150px" }}
                          ></div>
                        </div>
                      </div>
                      <div className="land-bar-priority">
                        <div
                          className="placeholder"
                          style={{
                            height: "24px",
                            width: "80px",
                            borderRadius: "12px",
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className="land-bar-center">
                      <div className="land-bar-stats">
                        <div className="bar-stat-item">
                          <div
                            className="placeholder"
                            style={{
                              height: "12px",
                              width: "60px",
                              marginBottom: "4px",
                            }}
                          ></div>
                          <div
                            className="placeholder"
                            style={{ height: "16px", width: "80px" }}
                          ></div>
                        </div>
                        <div className="bar-stat-item">
                          <div
                            className="placeholder"
                            style={{
                              height: "12px",
                              width: "60px",
                              marginBottom: "4px",
                            }}
                          ></div>
                          <div
                            className="placeholder"
                            style={{ height: "16px", width: "80px" }}
                          ></div>
                        </div>
                        <div className="bar-stat-item">
                          <div
                            className="placeholder"
                            style={{
                              height: "12px",
                              width: "60px",
                              marginBottom: "4px",
                            }}
                          ></div>
                          <div
                            className="placeholder"
                            style={{ height: "16px", width: "80px" }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    <div className="land-bar-right">
                      <div className="land-bar-actions">
                        <div
                          className="placeholder"
                          style={{
                            height: "32px",
                            width: "100px",
                            borderRadius: "6px",
                            marginRight: "8px",
                          }}
                        ></div>
                        <div
                          className="placeholder"
                          style={{
                            height: "32px",
                            width: "100px",
                            borderRadius: "6px",
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center text-muted">لا توجد نتائج</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {items.map((it) => (
                <LandCard key={`${it.id}-${it.sequence}`} item={it} onViewMap={openMap} />
              ))}
            </div>
          )}
        </div>

        {/* pager (DGA/Tailwind – same as Mosque) */}
        {pageCount > 1 && (
          <div className="flex items-center justify-center gap-3 pt-4">
            <button
              className="h-10 px-4 rounded-lg border border-neutral-300 text-neutral-700 text-sm hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              disabled={pageNo <= 1}
              onClick={() => go(pageNo - 1)}
            >
              السابق
            </button>
            <div className="text-neutral-500 text-sm">
              صفحة {pageNo} من {pageCount}
            </div>
            <button
              className="h-10 px-4 rounded-lg border border-neutral-300 text-neutral-700 text-sm hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              disabled={pageNo >= pageCount}
              onClick={() => go(pageNo + 1)}
            >
              التالي
            </button>
          </div>
        )}

        {/* page-size dropdown (DGA/Tailwind – same as Mosque) */}
        <div className="flex items-center justify-center gap-2 pt-2">
          <label className="text-sm text-neutral-500">حجم الصفحة</label>
          <select
            className="h-9 rounded-lg border border-neutral-300 bg-white text-sm text-neutral-700 focus:outline-none focus:ring-2 focus:ring-sa-500 min-w-[5ch]"
            style={{ width: "auto" }}
            value={pageSize}
            onChange={(e) => changePageSize(parseInt(e.target.value, 10))}
          >
            <option value={12}>12</option>
            <option value={24}>24</option>
          </select>
        </div>

              {/* map modal */}
              {(() => {
                const toNumOrNull = (v: any): number | null => {
                  if (v === null || v === undefined || v === "") return null;
                  const n = Number(v);
                  return Number.isFinite(n) ? n : null;
                };

                const lat = toNumOrNull((mapItem as any)?.latitude);
                const lng = toNumOrNull((mapItem as any)?.longitude);

                return (
                  <MapModal
                    // keep compatibility with different MapModal prop names
                    {...({
                      open: !!mapItem,
                      isOpen: !!mapItem,
                      onClose: closeMap,
                      onRequestClose: closeMap,
                      lat,
                      lng,
                      title: mapItem?.fullAddress ?? "",
                    } as any)}
                  />
                );
              })()}

      </div>
    </div>
  );
};

export default LandListPage;
